// The control that makes the language a choice.
//
// It lives in the ACCOUNT MENU rather than in the topbar, because which language you
// read in is a fact about you and not about the screen you are on — the same category as
// which company you are in and how you sign out, both of which are already here. A
// permanent control in the bar would spend the most valuable strip on the screen on a
// decision most people make once.
//
// A RADIO GROUP, not a toggle. Two locales ship today and a toggle would be the same
// control; it would also be the wrong shape the day a third arrives, and — more
// immediately — a toggle cannot say which language is CURRENT to somebody who cannot see
// which of two labels is emphasised. The radio group announces the state.
//
// Each language is named IN ITSELF: "Türkçe" and "English", never "Turkish" and
// "İngilizce". A reader who has landed in the wrong language is exactly the reader who
// cannot read the list, and a language named in a language they do not have is a list
// they cannot use. This is why `locale.tr` and `locale.en` are identical in both halves
// of the catalogue, which is otherwise the shape of a missing translation.

import { LanguagesIcon } from "lucide-react";
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { LOCALES, type Locale, useLanguage, useT } from "../shell/i18n";
import { useLocaleChoice } from "../shell/i18n/choice";

/** The message key that names a locale in its own language. */
const NAMED: Record<Locale, "locale.en" | "locale.tr"> = { en: "locale.en", tr: "locale.tr" };

/**
 * Picks the reader's language, or renders NOTHING where the language is not theirs to
 * pick.
 *
 * The careers surface is the case: its locale is in the URL, so a switcher there would
 * be a control that disagrees with the address bar. `useLocaleChoice` answers `null`
 * there and this renders nothing rather than a control that does not work.
 */
export function LanguageChoice() {
  const t = useT();
  const language = useLanguage();
  const choose = useLocaleChoice();

  if (!choose) {
    return null;
  }

  return (
    <>
      {/* THE LABEL IS INSIDE THE RADIO GROUP, and outside it this THREW.

          `DropdownMenuLabel` is Base UI's `Menu.GroupLabel`, which reads
          `MenuGroupContext` and raises when there is none — "Menu group parts must be
          used within <Menu.Group> or <Menu.RadioGroup>". It sat as a SIBLING of the
          group it names, so opening the account menu was an uncaught throw that took
          the whole application to the router's error screen. Nothing caught it in a
          test, because nothing opened this menu.

          Inside is also where it belongs semantically rather than merely legally: a
          group label is the accessible NAME of the group, so "Language" now names the
          three radio items instead of floating above them as decoration. */}
      <DropdownMenuRadioGroup value={language} onValueChange={(next) => choose(next as Locale)}>
        <DropdownMenuLabel className="flex items-center gap-2 font-normal text-muted-foreground">
          <LanguagesIcon className="size-4" aria-hidden="true" />
          {t("locale.label")}
        </DropdownMenuLabel>
        {LOCALES.map((locale) => (
          <DropdownMenuRadioItem
            key={locale}
            value={locale}
            // The menu closes on the press. Base UI keeps a radio menu open by default,
            // which is right for a filter somebody sets several of and wrong here: the
            // reader would be left looking at a menu whose every label has just changed
            // language underneath them, which reads as the screen glitching rather than
            // as the choice taking effect.
            closeOnClick
          >
            {t(NAMED[locale])}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  );
}
